import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { merge } from 'lodash';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  concatMap,
  exhaustMap,
  filter,
  finalize,
  mergeMap,
  pipe,
  tap
} from 'rxjs';

import { Account } from '@accounts/models/account';
import { AccountType } from '@accounts/models/account-type';
import { AccountsService } from '@accounts/services/accounts.service';
import { DEFAULT_RETRIEVAL_OPTIONS } from '@shared/consts/form';
import { PaginatedItems } from '@shared/models/paginated-items';
import { RetrievalOptions } from '@shared/models/retrieval-options';
import { ToastNotificationsService } from '@shared/services/toast-notifications.service';
import {
  isEmailAlreadyVerifiedError,
  systemAdminsErrorMessage
} from '@system-admins/stores/system-admins.store-helpers';

interface SystemAdminsState {
  data: Account[];

  loading: boolean;
  loaded: boolean;
  saving: boolean;
  error: HttpErrorResponse | null;

  pageSize: number;
  currentPageNumber: number;
  totalItemsCount: number;

  searchText: string | null;

  deleteSystemAdminIds: Set<string>;
  sendVerificationEmailIds: Set<string>;
  revokeMfaInProgressIds: Set<string>;
}

const INITIAL_STATE: SystemAdminsState = {
  data: [],

  loading: false,
  loaded: false,
  saving: false,
  error: null,

  pageSize: 50,
  currentPageNumber: 0,
  totalItemsCount: 0,

  searchText: null,

  deleteSystemAdminIds: new Set<string>(),
  sendVerificationEmailIds: new Set<string>(),
  revokeMfaInProgressIds: new Set<string>()
};

export const SYSTEM_ADMINS_STORE = signalStore(
  withState<SystemAdminsState>(INITIAL_STATE),

  withMethods(
    (
      store,
      accountsService = inject(AccountsService),
      toastNotificationsService = inject(ToastNotificationsService)
    ) => ({
      list: rxMethod<{
        retrievalOptions: RetrievalOptions;
      }>(
        pipe(
          tap(({ retrievalOptions }) => {
            patchState(store, {
              currentPageNumber: retrievalOptions.pageNumber,
              loading: true,
              loaded: false,
              error: null
            });
          }),

          exhaustMap(({ retrievalOptions }) => {
            const options = merge(
              {},
              DEFAULT_RETRIEVAL_OPTIONS,
              retrievalOptions
            );

            return accountsService.list(options, AccountType.SystemAdmin).pipe(
              tapResponse({
                next: (result: PaginatedItems<Account>) => {
                  patchState(store, {
                    data: result.items ?? [],
                    loading: false,
                    loaded: true,
                    currentPageNumber: result.currentPageNumber,
                    totalItemsCount: result.totalItemsCount
                  });
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    systemAdminsErrorMessage(error, 'Loading admins failed')
                  );

                  patchState(store, {
                    loading: false,
                    loaded: true,
                    error
                  });
                }
              })
            );
          })
        )
      ),

      create: rxMethod<Account>(
        pipe(
          tap(() => patchState(store, { saving: true, error: null })),

          exhaustMap(account =>
            accountsService.create(account).pipe(
              tapResponse({
                next: (result: Account) => {
                  toastNotificationsService.success('Admin created');

                  patchState(store, {
                    data: [result, ...store.data()],
                    saving: false
                  });
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    systemAdminsErrorMessage(error, 'Create admin failed')
                  );

                  patchState(store, {
                    error,
                    saving: false
                  });
                }
              })
            )
          )
        )
      ),

      update: rxMethod<Account>(
        pipe(
          tap(() => patchState(store, { saving: true, error: null })),

          concatMap(account =>
            accountsService.update(account).pipe(
              tapResponse({
                next: (result: Account) => {
                  toastNotificationsService.success('Admin updated');

                  patchState(store, {
                    data: store
                      .data()
                      .map(x => (x.id === result.id ? { ...x, ...result } : x)),
                    saving: false
                  });
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    systemAdminsErrorMessage(error, 'Update admin failed')
                  );

                  patchState(store, {
                    error,
                    saving: false
                  });
                }
              })
            )
          )
        )
      ),

      delete: rxMethod<string>(
        pipe(
          filter(
            systemAdminId => !store.deleteSystemAdminIds().has(systemAdminId)
          ),

          tap(systemAdminId =>
            patchState(store, {
              deleteSystemAdminIds: new Set([
                ...store.deleteSystemAdminIds(),
                systemAdminId
              ])
            })
          ),

          concatMap(systemAdminId =>
            accountsService.delete(systemAdminId).pipe(
              tapResponse({
                next: () => {
                  toastNotificationsService.success('Admin deleted');

                  patchState(store, {
                    data: store.data().filter(x => x.id !== systemAdminId)
                  });
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    systemAdminsErrorMessage(error, 'Delete admin failed')
                  );
                }
              }),
              finalize(() => {
                patchState(store, {
                  deleteSystemAdminIds: new Set(
                    [...store.deleteSystemAdminIds()].filter(
                      id => id !== systemAdminId
                    )
                  )
                });
              })
            )
          )
        )
      ),

      sendVerificationEmail: rxMethod<string>(
        pipe(
          filter(
            systemAdminId =>
              !store.sendVerificationEmailIds().has(systemAdminId)
          ),

          tap(systemAdminId =>
            patchState(store, {
              sendVerificationEmailIds: new Set([
                ...store.sendVerificationEmailIds(),
                systemAdminId
              ])
            })
          ),

          mergeMap(systemAdminId =>
            accountsService.sendVerificationEmail(systemAdminId).pipe(
              tapResponse({
                next: () => {
                  toastNotificationsService.success('Email verification sent');
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    systemAdminsErrorMessage(
                      error,
                      'Send email verification failed'
                    )
                  );

                  if (isEmailAlreadyVerifiedError(error)) {
                    patchState(store, {
                      data: store.data().map(x =>
                        x.id === systemAdminId
                          ? ({
                              ...x,
                              user: {
                                ...x.user,
                                emailVerified: true
                              }
                            } as Account)
                          : x
                      )
                    });
                  }
                }
              }),
              finalize(() => {
                patchState(store, {
                  sendVerificationEmailIds: new Set(
                    [...store.sendVerificationEmailIds()].filter(
                      id => id !== systemAdminId
                    )
                  )
                });
              })
            )
          )
        )
      ),

      revokeMfa: rxMethod<string>(
        pipe(
          filter(
            systemAdminId => !store.revokeMfaInProgressIds().has(systemAdminId)
          ),

          tap(systemAdminId =>
            patchState(store, {
              revokeMfaInProgressIds: new Set([
                ...store.revokeMfaInProgressIds(),
                systemAdminId
              ])
            })
          ),

          mergeMap(systemAdminId =>
            accountsService.revokeMfa(systemAdminId).pipe(
              tapResponse({
                next: () => {
                  toastNotificationsService.success(
                    'Re-register MFA requested'
                  );
                },

                error: (error: HttpErrorResponse) => {
                  toastNotificationsService.error(
                    systemAdminsErrorMessage(
                      error,
                      'Re-register MFA request failed'
                    )
                  );
                }
              }),
              finalize(() => {
                patchState(store, {
                  revokeMfaInProgressIds: new Set(
                    [...store.revokeMfaInProgressIds()].filter(
                      id => id !== systemAdminId
                    )
                  )
                });
              })
            )
          )
        )
      ),

      clearSystemAdmins(): void {
        patchState(store, { ...INITIAL_STATE });
      },

      updateSearchText(value: string): void {
        patchState(store, { searchText: value });
      },

      filterBySearchText(): void {
        const searchText = store.searchText()?.toLowerCase();

        if (!searchText) {
          return;
        }

        patchState(store, {
          data: store
            .data()
            .filter(x =>
              `${x.firstName} ${x.lastName} ${x.user?.email}`
                .toLowerCase()
                .includes(searchText)
            )
        });
      },

      clearSearchText(): void {
        patchState(store, { searchText: null });
      }
    })
  )
);
